import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { authApi, formatApiError } from "../AuthContext";
import Modal, { ConfirmDialog } from "../Modal";

const empty = { name: "", branch_slug: "", age_range: "", level: "", coach: "", capacity: 16, schedule: "", fee: "Bilgi Alınız" };

export default function CoursesSection() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => authApi.get("/admin/courses").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const onDelete = async () => {
    await authApi.delete(`/admin/courses/${confirmDel.id}`);
    toast.success("Kurs silindi");
    load();
  };

  return (
    <div data-testid="admin-courses">
      <SectionHeader title="Kurslar" sub="Spor kurslarını ekle, düzenle veya kaldır." onAdd={() => setEditing({ ...empty })} addLabel="Yeni Kurs" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.id} className="card-elev p-5" data-testid={`admin-course-${c.id}`}>
            <div className="font-display text-xl font-bold uppercase tracking-tight">{c.name}</div>
            <div className="text-xs text-muted-foreground mt-1">{c.branch_slug}</div>
            <ul className="mt-3 text-sm space-y-1 text-muted-foreground">
              <li>Yaş: {c.age_range}</li>
              <li>Seviye: {c.level}</li>
              <li>Antrenör: {c.coach}</li>
              <li>Program: {c.schedule}</li>
              <li>Kontenjan: {c.capacity} · Ücret: {c.fee}</li>
            </ul>
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => setEditing(c)} className="btn-ghost text-xs px-2 py-1.5" data-testid={`course-edit-${c.id}`}>
                <Edit2 className="h-3 w-3" /> Düzenle
              </button>
              <button type="button" onClick={() => setConfirmDel(c)} className="btn-ghost text-xs px-2 py-1.5 text-destructive hover:text-destructive" data-testid={`course-delete-${c.id}`}>
                <Trash2 className="h-3 w-3" /> Sil
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && <CourseForm item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <ConfirmDialog open={!!confirmDel} onClose={() => setConfirmDel(null)} onConfirm={onDelete} title="Kursu sil?" body={confirmDel ? `${confirmDel.name} kursu silinecek.` : ""} />
    </div>
  );
}

function CourseForm({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const isNew = !item.id;
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, capacity: parseInt(form.capacity, 10) || 0 };
      if (isNew) await authApi.post("/admin/courses", payload);
      else await authApi.put(`/admin/courses/${form.id}`, payload);
      toast.success(isNew ? "Kurs eklendi" : "Kurs güncellendi");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={isNew ? "Yeni Kurs" : "Kursu Düzenle"} size="lg">
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="course-form">
        <Field label="Kurs Adı" required><input className="input-base" required value={form.name} onChange={update("name")} data-testid="cf-name" /></Field>
        <Field label="Branş Slug" required><input className="input-base" required value={form.branch_slug} onChange={update("branch_slug")} placeholder="örn: futbol" data-testid="cf-slug" /></Field>
        <Field label="Yaş Aralığı" required><input className="input-base" required value={form.age_range} onChange={update("age_range")} placeholder="örn: 8-12 Yaş" data-testid="cf-age" /></Field>
        <Field label="Seviye" required><input className="input-base" required value={form.level} onChange={update("level")} data-testid="cf-level" /></Field>
        <Field label="Antrenör" required><input className="input-base" required value={form.coach} onChange={update("coach")} data-testid="cf-coach" /></Field>
        <Field label="Kontenjan" required><input type="number" className="input-base" required value={form.capacity} onChange={update("capacity")} data-testid="cf-capacity" /></Field>
        <Field label="Gün/Saat" required><input className="input-base" required value={form.schedule} onChange={update("schedule")} placeholder="Pa-Çar 17:00" data-testid="cf-schedule" /></Field>
        <Field label="Ücret"><input className="input-base" value={form.fee} onChange={update("fee")} data-testid="cf-fee" /></Field>
        <div className="sm:col-span-2 flex justify-end gap-3 pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="btn-secondary">İptal</button>
          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-60" data-testid="cf-submit">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-input">{label}{required && <span className="text-primary"> *</span>}</span>
      {children}
    </label>
  );
}

function SectionHeader({ title, sub, onAdd, addLabel }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-7">
      <div>
        <h1 className="heading-lg">{title}</h1>
        <p className="text-sm text-muted-foreground mt-1">{sub}</p>
      </div>
      {onAdd && (
        <button type="button" onClick={onAdd} className="btn-primary" data-testid="section-add">
          <Plus className="h-4 w-4" /> {addLabel}
        </button>
      )}
    </div>
  );
}
